/**
 * Canal ao vivo do chat (SSE por fetch, com o token no cabeçalho). Uma conexão por aba;
 * ao receber "a conversa X mudou", atualiza as consultas do React Query. Reconecta sozinho.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { refreshSession } from "./api";

export interface LiveEvent { c: string; t: "msg" | "edit" | "read" | "conv" | "typing"; by?: string }
const listeners = new Set<(e: LiveEvent) => void>();
let running = false;
let qcRef: QueryClient | null = null;

async function loop(): Promise<void> {
  let wait = 2000;
  while (running) {
    const token = useAuth.getState().session?.accessToken;
    if (!token) { await new Promise((r) => setTimeout(r, 5000)); continue; }
    try {
      const res = await fetch("/api/v1/chat/stream", { headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" }, credentials: "include" });
      if (res.status === 401) { await refreshSession(); await new Promise((r) => setTimeout(r, 1000)); continue; }
      if (!res.ok || !res.body) throw new Error(String(res.status));
      wait = 2000;
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done || !running) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try { dispatch(JSON.parse(line.slice(6)) as LiveEvent); } catch { /* ignora */ }
        }
      }
    } catch { /* cai e tenta de novo */ }
    await new Promise((r) => setTimeout(r, wait));
    wait = Math.min(wait * 2, 30_000);
  }
}

function dispatch(e: LiveEvent): void {
  if (qcRef && e.t !== "typing") {
    void qcRef.invalidateQueries({ queryKey: ["chat-conversas"] });
    void qcRef.invalidateQueries({ queryKey: ["chat-resumo"] });
    void qcRef.invalidateQueries({ queryKey: ["chat-msgs", e.c] });
  }
  for (const l of listeners) l(e);
}

/** Liga o canal ao vivo (uma vez, no Shell). */
export function useLiveChat(): void {
  const qc = useQueryClient();
  useEffect(() => {
    qcRef = qc;
    if (!running) { running = true; void loop(); }
  }, [qc]);
}

export function onLive(fn: (e: LiveEvent) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
